/* eslint-disable @typescript-eslint/no-shadow */
import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { ReactComponent as ArrowRigthIcon } from '../../icons/arrowcircleright.svg';
import { ReactComponent as CheckIcon } from '../../icons/verify.svg';
import { CREATE_GREASE_TRAP } from '../../services/graphql/grease-traps/greaseTrapMutation';
import { greaseTrapSelectedOptionsAtom } from '../../services/recoil/atoms/greaseTrapSelectedOptions';
import VendorOptionsDropdown from '../atoms/Dropdowns/VendorOptionsDropdown';
import VendorDropdown from '../atoms/Dropdowns/VendorsDropdown';
import InputNumber from '../atoms/Inputs/InputNumber';
import InputOptionsCarrousel from '../atoms/Inputs/InputOptionsCarrousel';
import SignatureCanvas from '../atoms/Signature';
import { SmallLoader } from '../atoms/SmallLoader';
import AddDriverModalForm from './AddDriverModalForm';
import AddTruckModalForm from './AddTruckModalForm';

interface GreaseTrapFormData {
  vendor_id: string;
  driver_id: string;
  truck_id: string;
  gross_pounds: number;
  gallons_grease_received: number;
  traceability: 'Yes' | 'No';
}

function GreaseTrapServiceForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedOptions, setSelectedOptions] = useRecoilState(
    greaseTrapSelectedOptionsAtom,
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [signature, setSignature] = useState<File | null>(null);

  const [createGreaseTrap, { loading }] = useMutation(CREATE_GREASE_TRAP);

  const { handleSubmit, watch, control, setValue } =
    useForm<GreaseTrapFormData>({ defaultValues: {} });

  const { vendor_id: vendorId, gross_pounds: grossPounds } = watch();

  useEffect(() => {
    setValue('driver_id', '' as never);
    setValue('truck_id', '' as never);
  }, [vendorId]);

  useEffect(() => {
    const gallons = grossPounds
      ? Math.round((Number(grossPounds) / 8.1) * 100) / 100
      : undefined;
    setValue('gallons_grease_received', gallons as never);
  }, [grossPounds]);

  const onSubmit: SubmitHandler<GreaseTrapFormData> = async (data) => {
    createGreaseTrap({
      variables: {
        vendor_id: data.vendor_id,
        driver_id: data.driver_id,
        truck_id: data.truck_id,
        location_id: selectedOptions.location_id,
        gross_pounds: data.gross_pounds,
        gallons_grease_received: data.gallons_grease_received,
        traceability: data.traceability === 'Yes',
        registered_by: selectedOptions.registered_by,
        driver_signature: signature,
      },
      onCompleted: (res) => {
        if (res.createGreaseTrap?.id) {
          setOpenModal(true);
          setTimeout(() => {
            setOpenModal(false);
            navigate('/');
          }, 2000);
          setTimeout(() => {
            setSelectedOptions({
              registered_by: '',
              location_id: '',
              location: null,
              registered: null,
            });
          }, 3000);
        }
      },
    });
  };

  return (
    <>
      <form
        className="flex flex-col gap-6 overflow-hidden"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-6">
          <h2 className="text-lg font-semibold text-ReyesBlack">
            {t('grease-trap.vendor-details')}
          </h2>
        </div>
        <div>
          <VendorDropdown
            title={t('grease-trap.vendor-name')}
            control={control}
            id="vendor_id"
          />
        </div>
        <div>
          <VendorOptionsDropdown
            id="driver_id"
            title={t('grease-trap.driver-name')}
            vendorId={vendorId}
            type="drivers"
            control={control}
            CreateModal={AddDriverModalForm}
            setValue={(val: string) => setValue('driver_id', val as never)}
          />
        </div>
        <div>
          <VendorOptionsDropdown
            id="truck_id"
            title={t('grease-trap.select-truck')}
            vendorId={vendorId}
            type="trucks"
            control={control}
            CreateModal={AddTruckModalForm}
            setValue={(val: string) => setValue('truck_id', val as never)}
          />
        </div>
        <div className="flex flex-col gap-6">
          <h2 className="text-lg font-semibold text-ReyesBlack">
            {t('grease-trap.entry-data')}
          </h2>
        </div>
        <div>
          <InputNumber
            label={t('grease-trap.gross-pounds')}
            control={control}
            defaultValue=""
            inputField="gross_pounds"
            validations={{ required: t('global.required') }}
            suffix=" Lbs"
            decimalCount={0}
          />
        </div>
        <div>
          <InputNumber
            label={t('grease-trap.gallons-received')}
            control={control}
            defaultValue=""
            inputField="gallons_grease_received"
            suffix=" Gal"
            decimalCount={2}
            disabled
          />
        </div>
        <div>
          <InputOptionsCarrousel
            label={t('grease-trap.traceability')}
            control={control}
            options={['Yes', 'No']}
            inputField="traceability"
            validations={{ required: t('global.required') }}
          />
        </div>
        <SignatureCanvas onSignatureSave={setSignature} />
        <button
          disabled={loading}
          type="submit"
          id="SaveGreaseTrap"
          className="mt-2 inline-flex h-12 w-full items-center justify-start gap-2.5 rounded-xl bg-ReyesGreen px-2.5 py-2 text-center text-lg font-black text-white shadow-custom"
        >
          {loading ? (
            <SmallLoader size={30} />
          ) : (
            <>
              {t('grease-trap.register-grease-trap')}
              <ArrowRigthIcon />
            </>
          )}
        </button>
      </form>
      <Modal
        isOpen={openModal}
        onRequestClose={() => {}}
        appElement={document.getElementById('root') || undefined}
        style={{
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          content: {
            maxWidth: '450px',
            maxHeight: '200px',
            margin: 'auto',
            borderRadius: '20px',
          },
        }}
        contentLabel="Alert"
      >
        <div className="flex h-full flex-col items-center justify-center">
          <CheckIcon />
          <p className="text-lg font-medium">
            {t('grease-trap.create-success')}
          </p>
        </div>
      </Modal>
    </>
  );
}

export default GreaseTrapServiceForm;
